import { useInitData } from '../../../../hooks'
import {
  getBirdviewActivities,
  getBirdviewDocFlows,
} from '../../DashboardBirdview.api'
import {
  BIRDVIEW_NEEDS_INPUT_TILE_STUB,
  BirdviewSection,
} from '../../DashboardBirdview.constants'
import { BIRDVIEW_GRETA_SECTION_MOCK } from '../../DashboardBirdview.mock'
import {
  checkIsBirdviewChatsViewable,
  checkIsBirdviewDocFlowsViewable,
  checkIsBirdviewDwellerDocumentsViewable,
  checkIsBirdviewDwellerSkeleton,
  checkIsBirdviewLandlordRole,
  checkIsBirdviewOversightSkeleton,
} from '../../DashboardBirdview.utils'
import {
  getBirdviewNoProviderRequestsFilters,
  getBirdviewOpenRequestsFilters,
  getBirdviewOverdueRequestsFilters,
  getBirdviewUnassignedRequestsFilters,
  toBirdviewListSection,
} from '../../DashboardBirdview.utils.api'
import { useBirdviewAttentionLoaders } from '../useBirdviewAttentionLoaders'
import { useBirdviewDwellerLoaders } from '../useBirdviewDwellerLoaders'
import { useBirdviewMailboxLoaders } from '../useBirdviewMailboxLoaders'
import { useBirdviewPropertiesLoaders } from '../useBirdviewPropertiesLoaders'
import { useBirdviewRequestLoaders } from '../useBirdviewRequestLoaders'
import { useBirdviewTeamLoadLoaders } from '../useBirdviewTeamLoadLoaders'

import {
  getBirdviewActivitiesFilters,
  getBirdviewDocFlowsFilters,
  toBirdviewActivityItem,
  toBirdviewDocFlowItem,
} from './useBirdviewSectionLoaders.utils'

export function useBirdviewSectionLoaders(role) {
  const { sections: sectionsPermissions } = useInitData()

  const { getRequestsCountTile, getRequestsSection } =
    useBirdviewRequestLoaders()
  const { getCommunicationSection, getUnreadTile } = useBirdviewMailboxLoaders()
  const { getAttentionSection, loadAttentionGroup } =
    useBirdviewAttentionLoaders()
  const { getPropertiesSection } = useBirdviewPropertiesLoaders()
  const { getTeamLoadSection } = useBirdviewTeamLoadLoaders()
  const {
    getDwellerAnnouncementsSection,
    getDwellerDocumentsSection,
    getDwellerHomeSection,
    getDwellerMessagesSection,
    getDwellerObjectsSection,
    getDwellerRequestsSection,
  } = useBirdviewDwellerLoaders()

  async function getOverdueTile(params) {
    return getRequestsCountTile(getBirdviewOverdueRequestsFilters(params))
  }

  async function getNoProviderTile(params) {
    return getRequestsCountTile(getBirdviewNoProviderRequestsFilters(params))
  }

  async function getUnassignedTile(params) {
    return getRequestsCountTile(getBirdviewUnassignedRequestsFilters(params))
  }

  async function getBacklogTile(params) {
    return getRequestsCountTile(getBirdviewOpenRequestsFilters(params))
  }

  // TODO Replace the stub with the API count once the backend ships the
  // "needs my input" request filter.
  async function getNeedsInputTile() {
    return BIRDVIEW_NEEDS_INPUT_TILE_STUB
  }

  async function getServiceRequestsSection(params) {
    return getRequestsSection(getBirdviewOpenRequestsFilters(params))
  }

  // Nothing marks a request critical on the backend, so the card lists the
  // open ones that have run past their due date.
  async function getCriticalRequestsSection(params) {
    return getRequestsSection(getBirdviewOverdueRequestsFilters(params))
  }

  async function getActivitiesSection(params) {
    const { data } = await getBirdviewActivities(
      getBirdviewActivitiesFilters(params)
    )

    return toBirdviewListSection(data, toBirdviewActivityItem)
  }

  // TODO Greta's suggested actions have no endpoint yet, so the card runs on
  // the prototype fixture — drop DashboardBirdview.mock.js once it ships.
  async function getGretaSection() {
    return BIRDVIEW_GRETA_SECTION_MOCK
  }

  async function getDocFlowsSection() {
    const { data } = await getBirdviewDocFlows(getBirdviewDocFlowsFilters())

    return toBirdviewListSection(data, toBirdviewDocFlowItem)
  }

  // Messages and Documents are optional dweller sections, gated by the same
  // section permissions as the menu.
  const dwellerGetters = {
    [BirdviewSection.DwellerAnnouncements]: getDwellerAnnouncementsSection,
    ...(checkIsBirdviewDwellerDocumentsViewable(sectionsPermissions)
      ? { [BirdviewSection.DwellerDocuments]: getDwellerDocumentsSection }
      : null),
    ...(checkIsBirdviewChatsViewable(sectionsPermissions)
      ? { [BirdviewSection.DwellerMessages]: getDwellerMessagesSection }
      : null),
    [BirdviewSection.DwellerRequests]: getDwellerRequestsSection,
    ...(checkIsBirdviewLandlordRole(role)
      ? { [BirdviewSection.DwellerObjects]: getDwellerObjectsSection }
      : { [BirdviewSection.DwellerHome]: getDwellerHomeSection }),
  }

  const staffGetters = {
    [BirdviewSection.Activities]: getActivitiesSection,
    [BirdviewSection.Attention]: getAttentionSection,
    [BirdviewSection.Communication]: getCommunicationSection,
    ...(checkIsBirdviewDocFlowsViewable(sectionsPermissions)
      ? { [BirdviewSection.DocFlows]: getDocFlowsSection }
      : null),
    [BirdviewSection.Greta]: getGretaSection,
    [BirdviewSection.Properties]: getPropertiesSection,
    [BirdviewSection.TileOverdue]: getOverdueTile,
    [BirdviewSection.TileUnread]: getUnreadTile,
    ...(checkIsBirdviewOversightSkeleton(role)
      ? {
          [BirdviewSection.CriticalRequests]: getCriticalRequestsSection,
          [BirdviewSection.TeamLoad]: getTeamLoadSection,
          [BirdviewSection.TileBacklog]: getBacklogTile,
          [BirdviewSection.TileUnassigned]: getUnassignedTile,
        }
      : {
          [BirdviewSection.ServiceRequests]: getServiceRequestsSection,
          [BirdviewSection.TileNeedsInput]: getNeedsInputTile,
          [BirdviewSection.TileNoProvider]: getNoProviderTile,
        }),
  }

  return {
    getters: checkIsBirdviewDwellerSkeleton(role)
      ? dwellerGetters
      : staffGetters,
    loadAttentionGroup,
  }
}
